import {
    FileResponse,
    IReportRenderRequest,
    IReportTemplateWriteModel,
    ReportDataSet,
    ReportRenderRequest,
    ReportTemplateModel,
    ReportTemplateWriteModel,
} from "../api/APIClient";
import { reportsClient } from "../api/clients";
import { statusOf } from "../api/errors";

/**
 * Report templates, and rendering them.
 *
 * The invariant this slice exists inside: **reports read `MarkResult` and never compute one.**
 * `ReportingTests` fails the build if `ReportDataService` ever gains a calculation dependency. The
 * legacy system had two calculation paths - bulk, and on-the-fly inside report rendering - and they
 * produced different numbers for the same student. Nothing here may recompute anything.
 *
 * ⚠ `assessment:reports.read` and `.write` are **missing from ScadsysCore's IdentityPermissionPolicy
 * list**, so they cannot be granted per organisation yet. In practice only the Admin role reaches
 * these endpoints today, because the permission handler short-circuits on Admin. The screens are
 * built; the gap is ScadsysCore's to close.
 */

export type { ReportTemplateModel, ReportDataSet };

export const listTemplates = (includeArchived = false): Promise<ReportTemplateModel[]> => reportsClient.list(includeArchived);

export const getTemplate = (id: string): Promise<ReportTemplateModel> => reportsClient.get(id);

export const createTemplate = (model: IReportTemplateWriteModel): Promise<ReportTemplateModel> =>
    reportsClient.create(new ReportTemplateWriteModel(model));

export const updateTemplate = (id: string, model: IReportTemplateWriteModel): Promise<ReportTemplateModel> =>
    reportsClient.update(id, new ReportTemplateWriteModel(model));

export const archiveTemplate = (id: string): Promise<ReportTemplateModel> => reportsClient.archive(id);

export const deleteTemplate = (id: string): Promise<boolean> => reportsClient.delete(id);

/** The data a render would use, without rendering it. Straight out of stored results. */
export const previewReport = (request: IReportRenderRequest): Promise<ReportDataSet> =>
    reportsClient.preview(new ReportRenderRequest(request));

export const renderHtml = (request: IReportRenderRequest): Promise<FileResponse> =>
    reportsClient.renderHtml(new ReportRenderRequest(request));

export const renderPdf = (request: IReportRenderRequest): Promise<FileResponse> =>
    reportsClient.renderPdf(new ReportRenderRequest(request));

/**
 * PDF rendering goes through Playwright, which needs its browser binaries present on the host. When
 * they are missing the endpoint answers 503 with that instruction rather than throwing, so this is
 * an environment problem to surface plainly - not a bug in the template.
 */
export const isPlaywrightMissing = (error: unknown): boolean => statusOf(error) === 503;

export const PLAYWRIGHT_HINT = "PDF rendering needs Playwright's browsers on the server. Run `playwright install chromium` on that host.";

/** Hands the rendered file to the browser as a download. */
export function saveFile(file: FileResponse, fallbackName: string): void {
    const url = URL.createObjectURL(file.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.fileName || fallbackName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Revoking immediately can cancel the download in some browsers; a tick is enough.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Opens rendered HTML in a new tab rather than downloading it. */
export function openInNewTab(file: FileResponse): void {
    const url = URL.createObjectURL(file.data);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}
